import type React from 'react';
import type { AvailabilitySnapshot as DomainAvailabilitySnapshot, QuoteOverlapDetail as DomainQuoteOverlapDetail } from '../../../domain/inventory';
import type { ColumnConfig } from '../../../domain/itemsPanel/columnTypes';
import type { EventLineItemData, EventLineProductData, EventLineRoomAccessData, EventLineSectionData } from '../../../data/eventLines';
import type { FulfillmentReservationRecord } from '../../../data/reservationFulfillment';
import type { KitPricingMode } from './itemUtils';
import type { ItemsGridTreeShape } from './itemsGridTree';
import type { EventOptionParticipationByTargetId } from '../../../domain/options/participation';

export type Row = {
  id: string;
  event_id?: string;
  room_id?: string;
  date_ymd?: string;
  /**
   * Line-level intent for fulfillment. Defaults to `rent` for new rows but can be
   * flipped to `sell` or `cross_rent` to steer downstream workflows once those
   * flows are implemented.
   */
  action?: LineAction | null;
  kind?: string | null;
  item_id?: string | null;
  product_id?: string;
  sku?: string; // UI-only input for item_code or SKU text
  item_code?: string | null;
  product_code?: string | null;
  custom_name?: string | null;
  name?: string;
  description?: string;
  qty?: number;
  charge_days?: number;
  unit_price?: number;
  discount?: number;
  r2_allow_discount?: boolean | null;
  r2_discount_amount?: number | null;
  r2_pre_discount_amount?: number | null;
  r2_net_amount?: number | null;
  r2_net_discount_pct?: number | null;
  smart_unit_price?: number | null;
  smart_max_discount_pct?: number | null;
  smart_pricing_snapshot_id?: string | null;
  smart_pricing_reason?: string | null;
  smart_pricing_signals?: Record<string, unknown> | null;
  smart_discount_exception_status?: string | null;
  note?: string;
  quick_turn?: boolean;
  facets?: any;
  line_order?: number | null;
  section_id?: string | null;
  /** When true, section header row shows a subtotal of all lines in that section (UI-only for now). */
  section_show_subtotal?: boolean;
  non_printable?: boolean;
  not_discountable?: boolean;
  ad_hoc?: boolean;
  convert_status?: 'pending' | 'converted';
  converted_product_id?: string | null;
  order_line_id?: string | null;
  order_id?: string | null;
  order_line_day_room_id?: string | null;
  order_day_room_id?: string | null;
  order_day_id?: string | null;
  order_room_id?: string | null;
  event_line_id?: string | null;
  event_room_access_id?: string | null;
  room_key?: string | null;
  usage_room_date?: string | null;
  start_at?: string | null;
  end_at?: string | null;
  /** Line-level fill (pick) date override */
  fill_date?: string | null;
  /** Line-level fill (pick) time override */
  fill_time?: string | null;
  /** Line-level return date override */
  return_date?: string | null;
  /** Line-level return time override */
  return_time?: string | null;
  /** True if fill_date is anticipated (computed), false if explicitly set */
  fill_is_anticipated?: boolean;
  /** True if return_date is anticipated (computed), false if explicitly set */
  return_is_anticipated?: boolean;
  source?: 'quote_line_items' | 'order_lines' | 'event_labor' | 'event_lines';
  created_at?: string;
  updated_at?: string;
  kit_anchor?: boolean;
  kit_component?: boolean;
  kit_instance_id?: string | null;
  kit_parent_row_id?: string | null;
  kit_item_id?: string | null;
  kit_label?: string | null;
  kit_component_id?: string | null;
  kit_pricing_mode?: KitPricingMode | null;
  // Extended JOIN data from event_lines for custom columns
  items?: EventLineItemData | null;
  products?: EventLineProductData | null;
  event_room_access?: EventLineRoomAccessData | null;
  /** Section (global sections table); event_lines use sections */
  sections?: EventLineSectionData | null;
  event_line_custom_fields?: Array<{ column_key: string; field_value: string | null }> | null;
  /** Server-assigned rollup group ID for "All" view grouping and reordering */
  rollup_group_id?: string | null;
  /** Underlying event_line_ids represented by an ALL-view rollup row. */
  rollup_event_line_ids?: string[] | null;
  /** Underlying local row ids represented by an ALL-view rollup row. */
  rollup_child_row_ids?: string[] | null;
  /** Explicit child kinds represented by an ALL-view rollup row (UI-only). */
  rollup_source_kinds?: string[] | null;
  /** Effective catalog maximum carried by a synthetic All-view rollup row. */
  rollup_charge_days_cap?: number | null;
  /** Sibling event_line_ids whose reservations should display on this split/date row. */
  reservation_source_event_line_ids?: string[] | null;
  /** R2 contract line IIDs carried from import; preserved across copy/paste. */
  r2_contract_line_iid?: string[] | null;
  /** True when charge_days is an authoritative per-date source allocation. */
  source_charge_days_authoritative?: boolean | null;
  /** True when this row represents a filled reservation detail row (synthetic, read-only) */
  reservation_row?: boolean;
  /** True when the reservation detail row is a read-only item_event_link lifecycle row. */
  reservation_legacy_link?: boolean;
  /** Synthetic reservation expansion notice state (loading/error/empty). */
  reservation_detail_notice?: 'loading' | 'error' | 'empty';
  /** Human-readable reservation expansion notice. */
  reservation_detail_message?: string | null;
  /** Reservation ID for reservation detail rows */
  reservation_id?: string | null;
  /** Parent event_line_id that this reservation belongs to */
  reservation_parent_event_line_id?: string | null;
  /** Reservation status for reservation detail rows */
  reservation_status?: string | null;
  /** Reservation shipped quantity for reservation detail rows */
  reservation_shipped_qty?: number | null;
  /** Reservation returned quantity for reservation detail rows */
  reservation_returned_qty?: number | null;
  /** Asset ID for reservation detail rows */
  reservation_asset_id?: string | null;
  /** Asset barcode for reservation detail rows */
  reservation_barcode?: string | null;
  /** Asset serial number for reservation detail rows */
  reservation_serial?: string | null;
};

export type LineAction = 'rent' | 'sell' | 'cross_rent';

/**
 * Normalise any incoming action value to a concrete `LineAction` so UI and
 * persistence layers share the same defaulting behaviour. Defaults to `rent`
 * whenever the input is missing or unrecognised.
 */
export const normaliseLineAction = (value: unknown): LineAction => {
  if (typeof value === 'string') {
    const lowered = value.trim().toLowerCase();
    if (lowered === 'sell') return 'sell';
    if (lowered === 'cross_rent' || lowered === 'cross-rent') return 'cross_rent';
  }
  return 'rent';
};

export type CandidateSite = { location_id: string; name?: string | null };

export type RowAvailability = {
  status: string;
  best_item_id?: string | null;
  used_mix?: boolean;
};

export type RowHoldStatus = {
  rawStatus: string;
  label: 'Ample' | 'Low' | 'Unavailable';
  suggestsTransfer: boolean;
  candidateSites: CandidateSite[];
  offerId?: string | null;
  transferId?: string | null;
};

export type QuoteOverlapDetail = DomainQuoteOverlapDetail;

export type AvailabilitySnapshot = DomainAvailabilitySnapshot;

export type RowRendererData = {
  rows: Row[];
  gridWidths?: number[];
  rowWidth?: number;
  gridTemplate?: string | string[];
  registerRow?: (index: number) => (node: HTMLElement | null) => void;
  getRowHeight?: (index: number) => number;
  getRowOffset?: (index: number) => number;
  hasMeasuredRow?: (index: number) => boolean;
  updateRow: (id: string, patch: Partial<Row>, origin?: 'user' | 'server' | 'system') => void;
  persistDebounced: (id: string, options?: { immediate?: boolean; trigger?: 'blur' | 'debounce' | 'enter' | 'system' }) => void;
  openMenu: (rowId: string, anchor: HTMLElement) => void;
  isSuperAdmin: boolean;
  adminCheckAvailability: (row: Row) => void;
  avail: Record<string, RowAvailability>;
  chargeDaysSupported: boolean;
  reorderRow?: (id: string, delta: number) => void;
  changeSection?: (id: string, dir: -1 | 1) => void;
  holdStatuses: Record<string, RowHoldStatus>;
  requestTransfer: (row: Row) => void;
  acceptTransferOffer: (row: Row) => void;
  getHoldKey: (row: Row) => string;
  availabilitySnapshots: Record<string, AvailabilitySnapshot>;
  /** True while the batch availability query is in flight (initial load or refetch). */
  availabilityLoading?: boolean;
  /** User-facing load failure state; missing snapshots stay unknown while set. */
  availabilityErrorMessage?: string | null;
  /** True when rows are aggregated across all usage dates. Availability remains date-scoped in this mode. */
  allDaysMode?: boolean;
  openAvailabilityModal: (row: Row) => void;
  showUsageDateColumn?: boolean;
  usageDateOptions?: Array<{ value: string; label: string }>;
  formatUsageDate?: (value: string | null | undefined) => string;
  defaultUsageDate?: string | null;
  /** Event line IDs that should be visually marked as the target of an AI suggestion or option baseline. */
  highlightEventLineIdSet?: Set<string>;
  /** Saved event options keyed by their baseline event_line IDs. */
  optionParticipationByEventLineId?: EventOptionParticipationByTargetId;
  /** Show Fill Date column in items panel */
  showFillDateColumn?: boolean;
  /** Show Fill Time column in items panel */
  showFillTimeColumn?: boolean;
  /** Show Return Date column in items panel */
  showReturnDateColumn?: boolean;
  /** Show Return Time column in items panel */
  showReturnTimeColumn?: boolean;
  /** Show Availability column in items panel (hidden for past / post-on-rent events) */
  showAvailabilityColumn?: boolean;
  /** Event-level anticipated fill date (YYYY-MM-DD) to use as default for lines without dates */
  anticipatedFillDate?: string | null;
  /** Event-level anticipated fill time (HH:MM:SS) to use as default for lines without dates */
  anticipatedFillTime?: string | null;
  /** Event-level anticipated return date (YYYY-MM-DD) to use as default for lines without dates */
  anticipatedReturnDate?: string | null;
  /** Event-level anticipated return time (HH:MM:SS) to use as default for lines without dates */
  anticipatedReturnTime?: string | null;
  selectedRowIds?: string[];
  isRowSelected?: (rowId: string) => boolean;
  /** Row checkbox toggle (rowId, index, event, row) - used when checkbox is rendered inside row */
  onRowCheckboxChange?: (rowId: string, index: number, event: React.ChangeEvent<HTMLInputElement>, row: Row) => void;
  /** Collect row ids in a section for section checkbox state */
  collectSectionRowIds?: (sectionIndex: number) => string[];
  /** Begin drag for reorder (rowId, event) - used when drag handle is inside row */
  beginDrag?: (rowId: string, event: React.PointerEvent) => boolean;
  logDebug?: (channel: string, message: string, detail?: any, level?: 'info' | 'warn' | 'error') => void;
  /** Apply a discount % to all item rows in a section (section header row id + new value) */
  applySectionDiscount?: (sectionRowId: string, discountPct: number) => void;
  /** Flush any pending section discount debounce for the given section */
  flushSectionDiscountPending?: (sectionRowId: string) => void;
  /** Handler for double-click on a row (e.g., to open rollup edit modal) */
  onRowDoubleClick?: (row: Row) => void;
  /** Precomputed section subtotal and avg discount per section header index (avoids O(n) loop per section) */
  sectionSubtotalsByIndex?: Map<number, { sectionSubtotal: number; sectionAvgDiscount: number }>;
  /** Kit expand/collapse: whether the given kit instance is expanded to show components */
  isKitExpanded?: (kitInstanceId: string) => boolean;
  /** Toggle kit expand/collapse by kit instance ID */
  onToggleKitExpand?: (kitInstanceId: string) => void;
  /** Number of component rows per kit instance ID (the "3 items" beside a kit's description) */
  kitComponentCountByInstanceId?: Record<string, number>;
  /** Tree connectors for kit parts and filled-asset rows, keyed by row id (see itemsGridTree.ts). */
  treeShapeByRowId?: Map<string, ItemsGridTreeShape>;
  /** A top-level row has an arrow, so every top-level row keeps the arrow's room and SKUs line up. */
  reserveTreeToggle?: boolean;
  /** Aggregated fulfillment by kit instance id (rolled up from component lines). */
  kitFulfillmentByInstanceId?: Record<
    string,
    {
      chargeOnly?: boolean;
      filled: number;
      shipped: number;
      returned: number;
      assetCount: number;
      componentCount: number;
      statusCounts: Record<'filled' | 'partial_filled' | 'shipped' | 'partial_shipped' | 'returned' | 'partial_returned' | 'reserved', number>;
    }
  >;
  /** Row whose description edit is active; used to avoid switching to note layout until focus leaves the row. */
  descriptionFocusRowId?: string | null;
  /** Called when description editing begins or its row loses focus (rowId or null). */
  onDescriptionFocusChange?: (rowId: string | null) => void;
  /** When set, the row with this id just had its note content cleared; restore focus to description input and clear. */
  contentJustClearedRowId?: string | null;
  /** Called when note content is cleared (so panel can set contentJustClearedRowId). */
  onNoteContentCleared?: (rowId: string) => void;
  /** Clear the contentJustClearedRowId after focus is restored. */
  onClearContentJustCleared?: () => void;
  // Dynamic columns for custom column rendering
  visibleColumns?: ColumnConfig[];
  /** Number of fleet vehicles assigned per event_line_id (for freight badge) */
  freightVehicleCountByLineId?: Record<string, number>;
  /** Whether a given reservation asset expansion key is expanded */
  isReservationExpanded?: (eventLineId: string) => boolean;
  /** Toggle reservation expand/collapse by expansion key */
  onToggleReservationExpand?: (eventLineId: string) => void;
  /** Number of reservations per event_line_id (for collapsed count label) */
  reservationCountByEventLineId?: Record<string, number>;
  /** Aggregated fulfillment counts per event_line_id */
  reservationAggregates?: Record<string, { filled: number; shipped: number; returned: number }>;
  /** Aggregated historical lifecycle counts per item_id for imported events without reservation assets. */
  itemLifecycleAggregatesByItemId?: Record<string, { filled: number; shipped: number; returned: number }>;
  /** Historical lifecycle asset count per item_id for imported events without reservation assets. */
  itemLifecycleCountByItemId?: Record<string, number>;
  /** Historical lifecycle asset rows per item_id for imported events without reservation assets. */
  itemLifecycleReservationsByItemId?: Map<string, FulfillmentReservationRecord[]>;
  /** Maps split/date sibling event_line_ids to the same reservation source set. */
  reservationSourceLineIdsByEventLineId?: Record<string, string[]>;
  /** True while expanded reservation detail rows are loading. */
  isReservationDetailsLoading?: boolean;
  /** Error message from loading expanded reservation detail rows. */
  reservationDetailsErrorMessage?: string | null;
  /** Whether the event is in picking stage or beyond (controls fulfillment columns/actions) */
  isPickingOrBeyond?: boolean;
  /** Whether filled asset history should be visible for the current event stage. */
  showFilledAssetHistory?: boolean;
  /** Whether reservation detail rows should expose stage actions. */
  reservationActionsEnabled?: boolean;
  /** Uses Customer Pickup labels and routes shipment through signed handoff. */
  customerPickup?: boolean;
  /** True when the event stage is invoiced (locks most fulfillment actions). */
  isEventInvoiced?: boolean;
  /** True when item/price editing is blocked by any workspace edit guard. */
  isWorkspaceEditLocked?: boolean;
  /** Room this panel renders, used as the drag source when moving rows between rooms. */
  dragSourceRoomId?: string | null;
  /** Current row selection, so dragging a selected row moves the whole selection. */
  dragSelectedRowIds?: string[];
  /** True when an invoiced event is temporarily unlocked for edits. */
  isInvoiceUnlocked?: boolean;
  /** Stage-aware primary action for reservation detail rows (e.g., Ship/Return). */
  onReservationPrimaryAction?: (row: Row) => void | Promise<void>;
  /** Label resolver for reservation detail row primary action button. */
  getReservationPrimaryActionLabel?: (row: Row) => string;
};

export type StoreAPI = {
  rows: Row[];
  setRows: React.Dispatch<React.SetStateAction<Row[]>>;
  getRow: (id: string) => Row | undefined;
  updateRow: (id: string, patch: Partial<Row>, origin?: 'user' | 'server' | 'system') => void;
  persistDebounced: (id: string, options?: { immediate?: boolean; trigger?: 'blur' | 'debounce' | 'enter' | 'system' }) => void;
};
